import type { EulerSwapResponse } from './types'

// =============================================================================
// Error message mapping
// =============================================================================

/**
 * Map a swap-related error (quote fetch or EVC simulation) to a user-friendly
 * "Quote not found" message, matching the euler-protocol-monorepo behaviour.
 *
 * Returns `null` for errors that should be handled differently (e.g.
 * E_AccountLiquidity → UndercollateralizedWarning) or that we don't recognise.
 */
/** Returns true when the error maps to "Quote not found" (all quotes failed or none available). */
export function isQuoteNotFoundError(error: Error | null | undefined): boolean {
  return getQuoteErrorMessage(error) === 'Quote not found'
}

export function getQuoteErrorMessage(error: Error | null | undefined): string | null {
  if (!error) return null

  const msg = error.message ?? ''

  // Account liquidity — return a user-friendly message here. Suppression for dialogs
  // that render their own UndercollateralizedWarning is handled upstream by
  // SwapErrorDisplay's `suppressAccountLiquidity` prop (returns null before reaching this).
  if (msg.includes('34373fbc') || msg.includes('E_AccountLiquidity')) {
    return 'Account liquidity check failed. Your position may become undercollateralized.'
  }

  if (msg.includes('Token in not supported') || msg.includes('Token out not supported') || msg.includes('Token not supported')) {
    return 'This token is not supported for swaps.'
  }

  // Direct "Quote not found" from useSimulatedBestQuote (all quotes failed simulation)
  // or from the quote API returning no results.
  if (msg === 'Quote not found' || msg.includes('No swap quote available') || msg.includes('No valid swap quotes')) {
    return 'Quote not found'
  }

  // EVC batch simulation failure from the final useSimulateEVCBatchCall
  // (after a validated quote was already selected). This is a genuine simulation
  // error, NOT "Quote not found" — useSimulatedBestQuote handles that upstream.
  if (
    msg.includes('Simulate EVC Batch Call') ||
    msg.includes('batchSimulation') ||
    msg.includes('execution reverted')
  ) {
    return 'Transaction simulation failed. Please try again or adjust the amount.'
  }

  // Timeout
  if (msg.includes('timed out')) {
    return 'Swap quote request timed out. Please try again.'
  }

  if (msg.includes('Euler swap API error')) {
    return 'Swap service is temporarily unavailable. Please try again later.'
  }

  return null
}

// =============================================================================
// Constants
// =============================================================================

/** Routes with more hops than this trigger a warning. */
const MAX_EXPECTED_HOPS = 3

/**
 * When two quotes have output amounts within this relative tolerance (0.1 %),
 * prefer the simpler route (fewer multicall items) to reduce gas and slippage risk.
 */
const SIMILAR_OUTPUT_TOLERANCE = 0.001

// =============================================================================
// Route validation
// =============================================================================

export interface RouteValidationResult {
  /** Whether the quote passed all checks. */
  valid: boolean
  /** Human-readable warnings (empty when valid). */
  warnings: string[]
}

/**
 * Validate basic invariants of a swap route returned by the Euler swap API.
 *
 * Currently checks:
 * - Route does not have an unusually high number of hops.
 * - swap / verify data is present when expected.
 *
 * This is intentionally a **soft** check – it logs warnings rather than
 * rejecting quotes outright – so it can be extended with whitelist checks
 * (e.g. verifying `swap.swapperAddress` against known Euler contracts) as
 * the protocol adds on-chain registries.
 */
export function validateSwapRoute(quote: EulerSwapResponse): RouteValidationResult {
  const warnings: string[] = []

  // --- hop count ---
  const hops = quote.route?.length ?? 0
  if (hops > MAX_EXPECTED_HOPS) {
    warnings.push(
      `Swap route has ${hops} hops (expected ≤ ${MAX_EXPECTED_HOPS}). ` +
        `Route providers: ${quote.route.map((r) => r.providerName).join(' → ')}`,
    )
  }

  // --- multicall item count ---
  const multicallItems = quote.swap?.multicallItems?.length ?? 0
  if (multicallItems > 10) {
    warnings.push(`Swap has ${multicallItems} multicall items – unusually high.`)
  }

  // --- log warnings (always, so production monitoring can pick them up) ---
  if (warnings.length > 0) {
    console.warn('[Euler swap route validation]', ...warnings, { quote })
  }

  return { valid: warnings.length === 0, warnings }
}

// =============================================================================
// Route selection
// =============================================================================

/**
 * Return all valid quotes sorted by preference (best first).
 *
 * Sorting strategy (transitive – safe for `Array.prototype.sort`):
 * 1. Filter out quotes with zero or invalid `amountOut`.
 * 2. Classify each quote as "top-tier" (within {@link SIMILAR_OUTPUT_TOLERANCE}
 *    of the global maximum output) or "lower-tier".
 * 3. Top-tier quotes come first; within the same tier sort by ascending
 *    multicall-item count, then descending output.
 *
 * Every returned quote is run through {@link validateSwapRoute} so
 * suspicious routes are logged.
 *
 * @throws {Error} When no valid quotes remain after filtering.
 */
export function sortQuotesByPreference(quotes: EulerSwapResponse[]): EulerSwapResponse[] {
  const validQuotes = quotes.filter((q) => {
    try {
      return q.amountOut != null && q.amountOut !== '' && BigInt(q.amountOut) > 0n
    } catch {
      return false
    }
  })

  if (validQuotes.length === 0) {
    throw new Error('No valid swap quotes available (all quotes had zero or invalid output)')
  }

  // Global maximum – used to determine top-tier membership.
  let maxOut = 0n
  for (const q of validQuotes) {
    const out = BigInt(q.amountOut)
    if (out > maxOut) maxOut = out
  }

  const toleranceInverse = BigInt(Math.round(1 / SIMILAR_OUTPUT_TOLERANCE))

  const isTopTier = (q: EulerSwapResponse) => {
    const diff = maxOut - BigInt(q.amountOut)
    return diff * toleranceInverse < maxOut
  }

  // Sort: top-tier first, then fewer multicall items, then higher output.
  const sorted = [...validQuotes].sort((a, b) => {
    const aTop = isTopTier(a)
    const bTop = isTopTier(b)
    if (aTop !== bTop) return aTop ? -1 : 1

    const aItems = a.swap?.multicallItems?.length ?? 0
    const bItems = b.swap?.multicallItems?.length ?? 0
    if (aItems !== bItems) return aItems - bItems

    // Higher output is better (descending).
    const aOut = BigInt(a.amountOut)
    const bOut = BigInt(b.amountOut)
    if (bOut > aOut) return 1
    if (aOut > bOut) return -1
    return 0
  })

  for (const q of sorted) validateSwapRoute(q)
  return sorted
}
